import { useEffect, useState } from 'react';
import apiClient from '../lib/api';
import { parseConcentration, parseVialMl, type ParsedConcentration } from '../utils/formularyParse';
import { presetKeyForFamilySlug } from '../data/peptidePresetSlugMap';
import { PRESETS, type PeptidePreset } from '../data/peptidePresets';

/** One purchasable SKU within a drug family, with parsed dose-math inputs. */
export interface FormularySku {
  productId: string;
  drugName: string;
  displayName: string;
  concentrationRaw: string;
  sizeRaw: string;
  conc: ParsedConcentration;
  vialMl: number | null;
  price: number | null;
  outOfStock: boolean;
  format: string;
  productType?: string;
  /** Concentration is per-mL AND we have a vial volume → full dose math works. */
  calcReady: boolean;
}

/** A drug family (e.g. semaglutide) grouping its real SKUs + optional preset. */
export interface FormularyFamily {
  slug: string;
  label: string;
  categorySlug?: string;
  presetKey?: string;
  preset?: PeptidePreset;
  skus: FormularySku[];
  calcReadyCount: number;
}

export interface FormularyData {
  families: FormularyFamily[];
  loading: boolean;
  error: string | null;
}

function titleizeSlug(slug: string): string {
  return slug
    .split('-')
    .map(w => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

function isInjectable(format?: string): boolean {
  return !!format && /inject/i.test(format);
}

/** Fetch the whole catalog (paginated, route caps limit at 100). */
async function fetchAllProducts(): Promise<any[]> {
  const all: any[] = [];
  let page = 1;
  // Hard stop well above the real catalog size as a runaway guard.
  for (let i = 0; i < 20; i++) {
    const res = await apiClient.getProducts({ page, limit: 100 });
    if (!res.success || !res.data) break;
    const products = res.data.products || [];
    all.push(...products);
    const pg = res.data.pagination || {};
    const hasNext = pg.hasNextPage ?? (pg.totalPages ? page < pg.totalPages : products.length === 100);
    if (!hasNext || products.length === 0) break;
    page += 1;
  }
  return all;
}

function toSku(p: any): FormularySku {
  const format = p.format || '';
  const conc = parseConcentration(p.concentration, { injectable: isInjectable(format) });
  const vialMl = parseVialMl(p.size);
  const price = typeof p.basePrice === 'number' ? p.basePrice
    : typeof p.price === 'number' ? p.price : null;
  return {
    productId: p._id,
    drugName: p.drugName || '',
    displayName: p.displayName || [p.drugName, p.concentration, p.size].filter(Boolean).join(' '),
    concentrationRaw: p.concentration || '',
    sizeRaw: p.size || '',
    conc,
    vialMl,
    price,
    outOfStock: p.outOfStock === true,
    format,
    productType: p.productType,
    calcReady: conc.calcReady && vialMl != null,
  };
}

function buildFamilies(products: any[]): FormularyFamily[] {
  const byFamily = new Map<string, FormularyFamily>();

  for (const p of products) {
    const slug = p.drugFamilySlug;
    if (!slug) continue; // un-categorized products don't belong to a family
    let fam = byFamily.get(slug);
    if (!fam) {
      const presetKey = presetKeyForFamilySlug(slug);
      const preset = presetKey ? PRESETS[presetKey] : undefined;
      fam = {
        slug,
        label: preset?.name || titleizeSlug(slug),
        categorySlug: p.primaryCategorySlug,
        presetKey,
        preset,
        skus: [],
        calcReadyCount: 0,
      };
      byFamily.set(slug, fam);
    }
    const sku = toSku(p);
    fam.skus.push(sku);
    if (sku.calcReady) fam.calcReadyCount += 1;
  }

  // Only families the calculator can actually work with: at least one
  // calc-ready injectable SKU, OR a known clinical preset.
  const families = [...byFamily.values()].filter(f => f.calcReadyCount > 0 || f.preset);

  // calc-ready SKUs first within each family; out-of-stock last.
  for (const f of families) {
    f.skus.sort((a, b) =>
      Number(b.calcReady) - Number(a.calcReady) ||
      Number(a.outOfStock) - Number(b.outOfStock) ||
      a.displayName.localeCompare(b.displayName)
    );
  }

  // Families with a preset first, then by label.
  families.sort((a, b) =>
    Number(!!b.preset) - Number(!!a.preset) || a.label.localeCompare(b.label)
  );
  return families;
}

/** Load the catalog once and shape it into calculator-ready families. */
export function useFormularyCompounds(): FormularyData {
  const [families, setFamilies] = useState<FormularyFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const products = await fetchAllProducts();
        if (cancelled) return;
        setFamilies(buildFamilies(products));
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load formulary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { families, loading, error };
}
